import React, { useEffect, useRef, useState } from 'react';
import { 
  FaBook, FaLaptop, FaNewspaper, FaBookOpen, FaGraduationCap, 
  FaArrowRight, FaCheckCircle, FaRegClock, FaUsers, FaWifi, 
  FaChair, FaDatabase, FaGlobe, FaMobileAlt, FaStar, FaSearch,
  FaUniversity, FaScroll, FaHeadphones, FaLaptopCode, FaPeace
} from 'react-icons/fa';
import { IoLibrary } from 'react-icons/io5';
import { MdOutlineQuietTime } from 'react-icons/md';

const LibrarySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: <FaBook />, title: "Vast Collection", desc: "Extensive collection of books covering diverse subjects", color: "from-yellow-400 to-yellow-500" },
    { icon: <FaNewspaper />, title: "Journals & References", desc: "Academic journals and reference materials for in-depth research", color: "from-orange-400 to-orange-500" },
    { icon: <FaLaptop />, title: "Digital Resources", desc: "Modern digital resources for interactive learning", color: "from-blue-400 to-blue-500" },
    { icon: <FaPeace />, title: "Peaceful Environment", desc: "Quiet and comfortable space for study and self-improvement", color: "from-green-400 to-green-500" }
  ];

  const resources = [
    { name: "Text Books", count: "10,000+", icon: <FaBookOpen /> },
    { name: "Reference Books", count: "5,000+", icon: <FaBook /> },
    { name: "E-Journals", count: "500+", icon: <FaNewspaper /> },
    { name: "Digital Resources", count: "2,000+", icon: <FaDatabase /> }
  ];

  const facilities = [
    { icon: <FaRegClock />, text: "8:00 AM - 8:00 PM (Mon-Sat)" },
    { icon: <FaUsers />, text: "Seating Capacity: 200+ Students" },
    { icon: <FaWifi />, text: "Free Wi-Fi Available" },
    { icon: <FaChair />, text: "Comfortable Reading Area" }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-yellow-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
            <div className="w-8 h-1 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
            <div className="w-6 h-1 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
          </div>

          {/* Breadcrumb */}
          <div className={`flex items-center justify-center gap-2 text-xs mb-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <span className="text-gray-500">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-orange-500 font-semibold">Library</span>
          </div>

          {/* Heading */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-3 rounded-2xl shadow-lg">
                <IoLibrary className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-yellow-500 via-orange-500 to-blue-500 bg-clip-text text-transparent">
                Central Library
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4 max-w-2xl mx-auto">
              Hub of knowledge and learning at BNS Mahila Maha Vidyalaya
            </p>
          </div>

          {/* Main Content */}
          <div className={`grid lg:grid-cols-2 gap-10 mb-12 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '100ms' }}>
            
            {/* Left Side - Description */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FaBookOpen className="text-yellow-500" />
                  About Our Library
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  The library at <span className="font-semibold text-orange-600">BNS Mahila Maha Vidyalaya, Akhari By-pass, Varanasi</span>, 
                  is a hub of knowledge and learning, offering an extensive collection of books, journals, 
                  and digital resources to support students' academic pursuits.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mt-3">
                  Designed to foster a conducive learning environment, our library provides a vast collection 
                  of books covering diverse subjects, academic journals for research, digital resources for 
                  modern learning, and a peaceful study space.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mt-3">
                  With its well-organized resources and research-friendly atmosphere, our library plays a 
                  crucial role in enriching students' educational experiences and fostering a love for 
                  lifelong learning.
                </p>
              </div>

              {/* Resources Stats */}
              <div className="grid grid-cols-2 gap-4">
                {resources.map((resource, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="text-yellow-500 text-2xl mb-2 flex justify-center">
                      {resource.icon}
                    </div>
                    <div className="text-xl font-bold text-gray-800">{resource.count}</div>
                    <div className="text-gray-500 text-xs">{resource.name}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Features */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <FaStar className="text-yellow-200" />
                  Library Features
                </h3>
                <div className="space-y-3">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                      <div className={`bg-gradient-to-r ${feature.color} p-2 rounded-lg text-white`}>
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm">{feature.title}</h4>
                        <p className="text-xs text-white/80">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Facilities Info */}
              <div className="bg-white rounded-2xl p-5 shadow-lg">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  Library Facilities
                </h3>
                <div className="space-y-2">
                  {facilities.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-gray-600 text-sm">
                      <div className="text-yellow-500">{item.icon}</div>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Library Highlights */}
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '200ms' }}>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaGlobe className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">Digital Library</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaMobileAlt className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">E-Books Access</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaSearch className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">Online Catalogue</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaGraduationCap className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">Research Support</p>
            </div>
          </div>

          {/* Quote Section */}
          <div className={`bg-gradient-to-r from-yellow-100 via-orange-50 to-blue-100 rounded-2xl p-6 text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '300ms' }}>
            <p className="text-gray-700 text-sm italic">
              "A library is not just a collection of books, but a gateway to knowledge, imagination, and lifelong learning."
            </p>
            <div className="flex justify-center gap-1 mt-2">
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
            </div>
          </div>

          {/* CTA Button */}
          <div className={`text-center mt-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '400ms' }}>
            <button className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto">
              Visit Library
              <FaArrowRight className="text-sm" />
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default LibrarySection;